import { useState, useRef, useEffect } from 'react'
import { AddAPhotoOutlined, Done, MusicNoteOutlined } from '@mui/icons-material'
import { Spin } from 'antd'
import './musicianImage.css'
import { motion } from 'framer-motion'
import Cropper from 'react-easy-crop'
import getCroppedImg from './cropImage'
import { useAtom } from 'jotai'
import { useAuth0 } from '@auth0/auth0-react'
import gcsClient from '../../../Api/Lirari/Clients/gcsClient'
import { useParams } from 'react-router-dom'
import { myUserAtom } from '../../../State/Musician/myUserAtom'
import { unauthenticatedUserAtom } from '../../../State/Musician/unauthenticatedUserAtom'
import userClient from '../../../Api/Lirari/Clients/userClient'

const MusicianImage = ({ editMode }: { editMode: boolean }) => {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [image, setImage] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [fileType, setFileType] = useState<string>('image/jpeg')
  const [audioFileType, setAudioFileType] = useState<string>('')

  const imageInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const [myUser, setMyUser] = useAtom(myUserAtom)
  const [unauthenticatedUser] = useAtom(unauthenticatedUserAtom)
  const { uid: userId } = useParams()

  const audioRef = useRef(new Audio())

  useEffect(() => {
    const audioUrl =
      (isAuthenticated && userId === myUser.id && myUser.profileAudioUrl) ||
      (userId && userId !== myUser.id && unauthenticatedUser.profileAudioUrl) ||
      'https://storage.googleapis.com/demo-profile/audio/Wartime%204.3.m4a'

    audioRef.current.src = audioUrl
  }, [isAuthenticated, userId, myUser.profileAudioUrl, unauthenticatedUser.profileAudioUrl, myUser.id])

  const randomDurations = Array.from({ length: 11 }, () => 0.7 + Math.random() * 0.2)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  const onCropComplete = async (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const handlePlayAudio = () => {
    const audio = audioRef.current
    if (isAudioPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
    setIsAudioPlaying(!isAudioPlaying)
  }

  const handleUploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        if (event.target?.result) {
          setImage(event.target.result.toString())
        }
      }
      reader.readAsDataURL(file)
      // Store the file type for later use
      setFileType(file.type)
    }
  }

  const handleUploadAudio = (event: any) => {
    const file = event.target.files?.[0]
    if (file) {
      setAudioFileType(file.type)
      uploadAudio(file)
    }
  }

  const uploadAudio = async (audioFile: File) => {
    if (!isAuthenticated || !userId || userId !== myUser.id) return

    try {
      setIsUploading(true)
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'user:all',
        },
      })

      // Get file extension from MIME type
      const extension = audioFileType.split('/')[1] || 'mp3'
      const fileName = `profile-audios/${myUser.id}/${Date.now()}.${extension}`

      // Get signed URL
      const signedUrl = await gcsClient.getSignedUrl(fileName, audioFileType, token)

      // Upload to GCS
      await gcsClient.uploadToSignedUrl(signedUrl, audioFile)

      // Update user profile with new audio URL
      const audioUrl = `https://storage.googleapis.com/${process.env.REACT_APP_GCS_BUCKET_NAME}/${fileName}`
      await userClient.updateUser(
        myUser.id,
        {
          profileAudioUrl: audioUrl,
        },
        token,
      )

      // Update local state
      const updatedUser = {
        ...myUser,
        profileAudioUrl: audioUrl,
      }
      setMyUser(updatedUser)

      // Update audio ref
      audioRef.current.src = audioUrl
    } catch (error) {
      console.error('Error uploading audio:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const uploadCroppedImage = async (croppedImageBlob: Blob) => {
    if (!isAuthenticated || !userId || userId !== myUser.id) return

    try {
      setIsUploading(true)
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'user:all',
        },
      })

      // Get file extension from MIME type
      const extension = fileType.split('/')[1] || 'jpg'
      const fileName = `profile-images/${myUser.id}/${Date.now()}.${extension}`

      // Get signed URL
      const signedUrl = await gcsClient.getSignedUrl(fileName, fileType, token)

      // Upload to GCS
      const file = new File([croppedImageBlob], fileName, { type: fileType })
      await gcsClient.uploadToSignedUrl(signedUrl, file)

      // Update user profile with new image URL
      const imageUrl = `https://storage.googleapis.com/${process.env.REACT_APP_GCS_BUCKET_NAME}/${fileName}`
      await userClient.updateUser(
        myUser.id,
        {
          profileImageUrl: imageUrl,
        },
        token,
      )

      // Update local state
      const updatedUser = {
        ...myUser,
        profileImageUrl: imageUrl,
      }
      setMyUser(updatedUser)
    } catch (error) {
      console.error('Error uploading image:', error)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="relative flex justify-center">
      <div className="hidden z-[-1] lg:flex left-0 translate-x-[-80%] top-1/2 translate-y-[-50%] lg:absolute items-center gap-x-[2vw]">
        {[3, 5, 9, 17, 20, 17, 22, 20, 35, 45, 35].map((length, index) => (
          <motion.div
            key={index}
            className="bg-white w-2 rounded-sm"
            animate={
              isAudioPlaying
                ? {
                    height: [
                      `${index}vh`,
                      `${Math.max(length - 10, 5)}vh`,
                      `${Math.max(length - 12, 5)}vh`,
                      `${Math.max(length - 8, 5)}vh`,
                      `${length}vh`,
                    ],
                  }
                : { height: `${index}vh` }
            }
            transition={
              isAudioPlaying
                ? {
                    duration: randomDurations[index],
                    repeat: Infinity,
                    repeatType: 'mirror',
                    ease: 'easeInOut',
                  }
                : { duration: randomDurations[index] }
            }
          />
        ))}
      </div>
      <div className="hidden z-[-1] lg:flex lg:absolute right-0 top-1/2 translate-x-[80%] translate-y-[-50%] flex-row-reverse items-center gap-x-[2vw]">
        {[3, 5, 9, 17, 20, 17, 22, 20, 35, 45, 35].map((length, index) => (
          <motion.div
            key={index}
            className="bg-white w-2 rounded-sm"
            animate={
              isAudioPlaying
                ? {
                    height: [
                      `${index}vh`,
                      `${Math.max(length - 10, 5)}vh`,
                      `${Math.max(length - 12, 5)}vh`,
                      `${Math.max(length - 8, 5)}vh`,
                      `${length}vh`,
                    ],
                  }
                : { height: `${index}vh` }
            }
            transition={
              isAudioPlaying
                ? {
                    duration: randomDurations[index],
                    repeat: Infinity,
                    repeatType: 'mirror',
                    ease: 'easeInOut',
                  }
                : { duration: randomDurations[index] }
            }
          />
        ))}
      </div>

      {isUploading ? (
        <div className="w-[80vw] h-[80vw] lg:w-[45vh] lg:h-[45vh] lg:min-w-[250px] lg:min-h-[250px] lg:max-w-[450px] lg:max-h-[450px] border-4 border-white flex items-center justify-center">
          <Spin size="large" tip="Uploading image..." />
        </div>
      ) : (
        <img
          className={`musician-image w-[80vw] h-[80vw] lg:w-[45vh] lg:h-[45vh] lg:min-w-[250px] lg:min-h-[250px] lg:max-w-[450px] lg:max-h-[450px] border-4 b-white`}
          onClick={() => !editMode && handlePlayAudio()}
          src={
            // Use own user data if authenticated and viewing own profile
            (isAuthenticated && userId === myUser.id && myUser.profileImageUrl) ||
            // Use unauthenticated user data when viewing someone else's profile
            (userId && userId !== myUser.id && unauthenticatedUser.profileImageUrl) ||
            // Fallback to default image
            'https://storage.googleapis.com/demo-profile/images/the-musician-unblurred.png'
          }
          alt="musician"
        />
      )}
      {editMode && image != '' ? (
        <div>
          <button
            className="size-14 absolute z-[99] flex items-center justify-center rounded-full bg-white hover:bg-[#cdcdcd]/90 cursor-pointer [&>svg]:text-black [&>svg]:size-7"
            onClick={async () => {
              setImage('')
              if (!croppedAreaPixels) {
                return
              }
              const croppedImage = await getCroppedImg(image, croppedAreaPixels)
              if (croppedImage) {
                // Convert base64 to blob
                const response = await fetch(croppedImage)
                const blob = await response.blob()
                await uploadCroppedImage(blob)
              }
            }}
            disabled={isUploading}
          >
            <Done />
          </button>
          <Cropper
            image={image}
            crop={crop}
            cropShape="round"
            zoom={zoom}
            aspect={1 / 1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </div>
      ) : editMode ? (
        <div className="upload-buttons">
          <input
            onChange={handleUploadAudio}
            multiple={false}
            ref={audioInputRef}
            type="file"
            accept="audio/*"
            hidden
            disabled={isUploading}
          />

          <button
            className="flex items-center justify-center rounded-full bg-white hover:bg-[#cdcdcd]/90 size-12 cursor-pointer [&>svg]:text-black [&>svg]:size-7"
            onClick={() => audioInputRef.current?.click()}
            disabled={isUploading}
          >
            <MusicNoteOutlined />
          </button>

          <input
            onChange={handleUploadImage}
            multiple={false}
            ref={imageInputRef}
            type="file"
            accept="image/*"
            hidden
            disabled={isUploading}
          />

          <button
            className="flex items-center justify-center rounded-full bg-white hover:bg-[#cdcdcd]/90 size-12 cursor-pointer [&>svg]:text-black [&>svg]:size-7"
            onClick={() => imageInputRef.current?.click()}
            disabled={isUploading}
          >
            <AddAPhotoOutlined />
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default MusicianImage
